// Whether the receiver can start a connection on a listed transmitter, and when not, why: the
// receiver's closed vocabularies, restated (the admin contract cannot import insurance) and pinned
// to the receiver's lists by Power's vocabulary-drift test.
import { Schema } from 'effect';

/** Why the receiver takes no connection on a listed transmitter (receiver UnavailableReason). */
export const OPIN_RECEIVER_UNAVAILABLE_REASONS = [
  'discovery-pending',
  'discovery-unreachable',
  'discovery-answered',
  'discovery-refused',
  'endpoint-missing',
  'profile-unresolvable',
  'profile-unsupported-preference',
  'profile-conflicting-preference',
  'tls-certificate-refused',
  'conformance-receiver',
] as const;
export const OpinReceiverUnavailableReason = Schema.Literals(OPIN_RECEIVER_UNAVAILABLE_REASONS);
export type OpinReceiverUnavailableReason = typeof OpinReceiverUnavailableReason.Type;

/**
 * Why the last rediscovery of a transmitter that stays available failed (receiver DiscoveryWarning):
 * it keeps its last good discovery, and the operator sees the failure as a warning.
 */
export const OPIN_RECEIVER_DISCOVERY_WARNINGS = [
  'discovery-unreachable',
  'discovery-answered',
  'discovery-refused',
] as const;
export const OpinReceiverDiscoveryWarning = Schema.Literals(OPIN_RECEIVER_DISCOVERY_WARNINGS);
export type OpinReceiverDiscoveryWarning = typeof OpinReceiverDiscoveryWarning.Type;

/** Why the last TLS probe of an available transmitter failed (receiver TlsWarning). */
export const OPIN_RECEIVER_TLS_WARNINGS = ['tls-unreachable'] as const;
export const OpinReceiverTlsWarning = Schema.Literals(OPIN_RECEIVER_TLS_WARNINGS);
export type OpinReceiverTlsWarning = typeof OpinReceiverTlsWarning.Type;

/** Whether a consent can be asked from the transmitter, and when not, why. */
export const OpinReceiverTransmitterAvailability = Schema.Union([
  Schema.Struct({
    state: Schema.Literal('available'),
    discoveryWarning: Schema.NullOr(OpinReceiverDiscoveryWarning),
    tlsWarning: Schema.NullOr(OpinReceiverTlsWarning),
  }),
  Schema.Struct({ state: Schema.Literal('unavailable'), reason: OpinReceiverUnavailableReason }),
]);
export type OpinReceiverTransmitterAvailability = typeof OpinReceiverTransmitterAvailability.Type;
